/**
 * Global multiplier system.
 *
 * Every rate in the game — production, build speed, flight speed, research —
 * passes through here before it is used. That gives one place to add a timed
 * buff ("Bautempo +30 % für 24 h") without touching a single formula, which is
 * the whole point: seasonal events, alliance bonuses (GDD §3 Dyson sphere:
 * "+15 % Megawatt-Energie sowie +10 % Schildstärke") and future premium boosts
 * all become data rather than code.
 *
 * Effects are stored in the save with an absolute expiry, so they survive
 * reloads and expire correctly across an offline stretch.
 */

import type { GameState } from './types.js';

/** What a bonus can act on. */
export type ModifierCategory =
  | 'production' // mine output
  | 'energy' // solar output
  | 'buildSpeed' // building construction
  | 'researchSpeed'
  | 'shipyardSpeed'
  | 'flightSpeed' // fleet travel and harvest round trips
  | 'storage'
  | 'combatDamage'
  | 'combatShield';

export const MODIFIER_CATEGORIES: ModifierCategory[] = [
  'production',
  'energy',
  'buildSpeed',
  'researchSpeed',
  'shipyardSpeed',
  'flightSpeed',
  'storage',
  'combatDamage',
  'combatShield',
];

/** German labels for the HUD. */
export const MODIFIER_LABELS: Record<ModifierCategory, string> = {
  production: 'Rohstoffgewinnung',
  energy: 'Energieausbeute',
  buildSpeed: 'Bautempo',
  researchSpeed: 'Forschungstempo',
  shipyardSpeed: 'Werfttempo',
  flightSpeed: 'Fluggeschwindigkeit',
  storage: 'Lagerkapazität',
  combatDamage: 'Waffenschaden',
  combatShield: 'Schildstärke',
};

/** A timed bonus, e.g. a weekend event or an alliance perk. */
export interface TimedEffect {
  id: number;
  /** Shown in the HUD, e.g. "Sektor-Festival". */
  label: string;
  category: ModifierCategory;
  /** Additive percentage: 30 means +30 %. Negative values are debuffs. */
  percent: number;
  startedAt: number;
  /** Absolute expiry (epoch ms). Use Infinity for permanent effects. */
  expiresAt: number;
}

/** Resolved multipliers — 1.0 means "no change". */
export type Modifiers = Record<ModifierCategory, number>;

export function neutralModifiers(): Modifiers {
  return {
    production: 1, energy: 1, buildSpeed: 1, researchSpeed: 1, shipyardSpeed: 1,
    flightSpeed: 1, storage: 1, combatDamage: 1, combatShield: 1,
  };
}

/**
 * Aggregate the active effects at `now`.
 *
 * Percentages within a category ADD before being applied (two +30 % events
 * give +60 %, not +69 %). Additive stacking keeps the numbers predictable for
 * players and keeps a stack of events from compounding into absurdity.
 */
export function computeModifiers(state: GameState, now: number): Modifiers {
  const mods = neutralModifiers();
  // `?? []` keeps pre-effects saves working before `deserialize` normalises them.
  for (const effect of state.effects ?? []) {
    if (now < effect.startedAt || now >= effect.expiresAt) continue;
    mods[effect.category] += effect.percent / 100;
  }
  // A debuff can never invert a rate.
  for (const key of MODIFIER_CATEGORIES) {
    mods[key] = Math.max(0.05, mods[key]);
  }
  return mods;
}

/** Effects that are live at `now`, for the HUD. */
export function activeEffects(state: GameState, now: number): TimedEffect[] {
  return (state.effects ?? []).filter((e) => now >= e.startedAt && now < e.expiresAt);
}

/**
 * The next moment an effect starts or ends, strictly AFTER `after`.
 *
 * The tick cuts its integration segments here, so a buff that expires halfway
 * through an offline stretch boosts exactly the hours it covered. Strictly
 * after, because unlike a finished job an effect edge is not consumed when the
 * tick visits it — returning `after` itself would spin the boundary loop.
 */
export function nextEffectBoundary(state: GameState, after: number): number | null {
  let best: number | null = null;
  for (const effect of state.effects ?? []) {
    for (const t of [effect.startedAt, effect.expiresAt]) {
      if (!Number.isFinite(t) || t <= after) continue;
      if (best === null || t < best) best = t;
    }
  }
  return best;
}

/** Drop expired effects. Mutates; called from the tick. */
export function pruneEffects(state: GameState, now: number): TimedEffect[] {
  const expired = (state.effects ?? []).filter((e) => now >= e.expiresAt);
  if (expired.length > 0) {
    state.effects = state.effects.filter((e) => now < e.expiresAt);
  }
  return expired;
}

/** Add a timed effect. Mutates. */
export function grantEffect(
  state: GameState,
  effect: Omit<TimedEffect, 'id'>,
): TimedEffect {
  const created = { ...effect, id: state.nextId++ };
  state.effects ??= [];
  state.effects.push(created);
  return created;
}
